/**
 * Email Subscribers Management Page
 * Manage subscribers in lists for email broadcast
 *
 * Created: January 3, 2026
 */

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Container, Row, Col, Badge, Card } from "react-bootstrap";
import Modal from "react-modal";
import { toast } from "react-toastify";

import { selectUser } from "../../../components/auth/auth.selector";
import HelmetWrapper from "../../../HelmetWrapper";
import CommonStructured from "../../../components/common-components/CommonStructured";
import CmnFilters from "../../../components/lead-management/common/CmnFilters";
import ModalHeadAndClose from "../../../components/payouts-wtf/common/ModalHeadAndClose";
import ReactVortoxSpinner from "../../../components/common-components/ReactVortoxSpinner";
import PaymentLinkTable from "../../../components/payment-link-system/PaymentLinkTable";

import {
  fetchEmailSubscribersData,
  fetchEmailListsData,
  fetchUserTypesData,
} from "../../../redux/email-broadcast/emailBroadcastSlice";
import {
  addEmailSubscriber,
  bulkAddEmailSubscribers,
  importEmailSubscribers,
  exportEmailSubscribers,
} from "../../../redux/email-broadcast/emailBroadcastApi";
import { formatDateTimezon } from "../../../components/common-data/Converts";
import CsvImportModal from "../../../components/email-broadcast/CsvImportModal";

// Add Subscriber Form
const SubscriberForm = ({ listId, closeModal, userInfoData, callFunction }) => {
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    phone: "",
    list_id: listId || "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email.trim()) {
      toast.error("Email is required");
      return;
    }

    setLoading(true);
    try {
      const response = await addEmailSubscriber(formData, userInfoData?.token);
      if (response?.status) {
        toast.success("Subscriber added successfully");
        callFunction();
        closeModal();
      } else {
        toast.error(response?.message || "Failed to add subscriber");
      }
    } catch (error) {
      toast.error(error.message || "Failed to add subscriber");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-3">
      <div className="mb-3">
        <label className="form-label">Email Address *</label>
        <input
          type="email"
          className="form-control"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          placeholder="subscriber@example.com"
          required
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Name</label>
        <input
          type="text"
          className="form-control"
          value={formData.name}
          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          placeholder="John Doe"
        />
      </div>

      <div className="mb-3">
        <label className="form-label">Phone</label>
        <input
          type="text"
          className="form-control"
          value={formData.phone}
          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          placeholder="9876543210"
        />
      </div>

      <div className="d-flex gap-2">
        <button type="submit" className="btn btn-primary flex-grow-1" disabled={loading}>
          {loading ? "Adding..." : "Add Subscriber"}
        </button>
        <button type="button" className="btn btn-secondary" onClick={closeModal}>
          Cancel
        </button>
      </div>
    </form>
  );
};

// Bulk Import Form
const BulkImportForm = ({ listId, closeModal, userInfoData, callFunction }) => {
  const dispatch = useDispatch();
  const { userTypes } = useSelector((state) => state.emailBroadcast || {});
  const [importType, setImportType] = useState("csv");
  const [userType, setUserType] = useState("");
  const [csvData, setCsvData] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(fetchUserTypesData({ token: userInfoData?.token }));
  }, []);

  const handleImport = async () => {
    setLoading(true);
    try {
      let response;
      if (importType === "csv") {
        // Parse CSV and bulk add
        const lines = csvData.trim().split("\n");
        const subscribers = lines.slice(1).map((line) => {
          const [email, name, phone] = line.split(",").map((s) => s.trim());
          return { email, name, phone, list_id: listId };
        }).filter((s) => s.email);

        response = await bulkAddEmailSubscribers(
          { list_id: listId, subscribers },
          userInfoData?.token
        );
      } else {
        // Import from user type
        response = await importEmailSubscribers(
          { list_id: listId, user_type: userType },
          userInfoData?.token
        );
      }

      if (response?.status) {
        toast.success(response.message || "Import successful");
        callFunction();
        closeModal();
      } else {
        toast.error(response?.message || "Import failed");
      }
    } catch (error) {
      toast.error(error.message || "Import failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-3">
      <div className="mb-3">
        <label className="form-label">Import Type</label>
        <div className="d-flex gap-3">
          <div className="form-check">
            <input
              type="radio"
              className="form-check-input"
              id="csv"
              checked={importType === "csv"}
              onChange={() => setImportType("csv")}
            />
            <label className="form-check-label" htmlFor="csv">CSV Data</label>
          </div>
          <div className="form-check">
            <input
              type="radio"
              className="form-check-input"
              id="usertype"
              checked={importType === "usertype"}
              onChange={() => setImportType("usertype")}
            />
            <label className="form-check-label" htmlFor="usertype">From WTF Users</label>
          </div>
        </div>
      </div>

      {importType === "csv" ? (
        <div className="mb-3">
          <label className="form-label">CSV Data</label>
          <textarea
            className="form-control font-monospace"
            rows={8}
            value={csvData}
            onChange={(e) => setCsvData(e.target.value)}
            placeholder="email,name,phone&#10;john@example.com,John Doe,9876543210&#10;jane@example.com,Jane Smith,9876543211"
          />
          <small className="text-muted">First row should be headers: email,name,phone</small>
        </div>
      ) : (
        <div className="mb-3">
          <label className="form-label">User Type</label>
          <select
            className="form-select"
            value={userType}
            onChange={(e) => setUserType(e.target.value)}
          >
            <option value="">Select user type...</option>
            {userTypes?.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label} ({type.count?.toLocaleString()} users)
              </option>
            ))}
          </select>
          <small className="text-muted">Import all users of this type from WTF database</small>
        </div>
      )}

      <div className="d-flex gap-2">
        <button
          className="btn btn-primary flex-grow-1"
          onClick={handleImport}
          disabled={loading || (importType === "csv" && !csvData) || (importType === "usertype" && !userType)}
        >
          {loading ? "Importing..." : "Import Subscribers"}
        </button>
        <button className="btn btn-secondary" onClick={closeModal}>
          Cancel
        </button>
      </div>
    </div>
  );
};

const EmailSubscribers = () => {
  const { listId } = useParams();
  const dispatch = useDispatch();
  const userInfoData = useSelector(selectUser);
  const { emailSubscribers, emailSubscribersLoading, emailLists } = useSelector(
    (state) => state.emailBroadcast || {}
  );

  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(20);

  const iniAllFilter = { searchString: "", status: "" };
  const [allFilters, setAllFilters] = useState(iniAllFilter);
  const [filters, setFilters] = useState({ page: 1, limit: 20, list_id: listId });
  const [searchFilterApplied, setSearchFilterApplied] = useState(false);

  const iniModValues = { isModOpen: false, modName: "", rowData: "" };
  const [modValues, setModValues] = useState(iniModValues);
  const [showCsvImport, setShowCsvImport] = useState(false);

  const currentList = emailLists?.data?.find((l) => l.uid === listId || l.id === parseInt(listId));

  const fetchSubscribers = (filters) => {
    dispatch(fetchEmailSubscribersData({ filters, token: userInfoData?.token }));
  };

  useEffect(() => {
    dispatch(fetchEmailListsData({ filters: {}, token: userInfoData?.token }));
  }, []);

  useEffect(() => {
    fetchSubscribers(filters);
  }, [filters]);

  useEffect(() => {
    setFilters((prev) => ({ ...prev, page: isPage + 1, limit: pageLimit }));
  }, [isPage, pageLimit]);

  const onClickSearch = () => {
    setIsPage(0);
    if (allFilters.searchString) {
      setSearchFilterApplied(true);
      setFilters((prev) => ({ ...prev, search: allFilters.searchString }));
    }
  };

  const onResetFilters = () => {
    setAllFilters(iniAllFilter);
    setFilters({ page: 1, limit: 20, list_id: listId });
  };

  const handleExport = async () => {
    try {
      const response = await exportEmailSubscribers({ list_id: listId, format: "csv" }, userInfoData?.token);
      if (response) {
        // Download CSV
        const blob = new Blob([response], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `subscribers_${listId}_${Date.now()}.csv`;
        a.click();
        toast.success("Export started");
      }
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      subscribed: "success",
      unsubscribed: "secondary",
      bounced: "danger",
      complained: "warning",
    };
    return <Badge bg={colors[status] || "secondary"}>{status}</Badge>;
  };

  const tbColumns = [
    {
      dataField: "email",
      text: "Email",
      formatter: (_, row) => (
        <div>
          <span className="fw-medium">{row.email}</span>
          {row.name && <small className="d-block text-muted">{row.name}</small>}
        </div>
      ),
    },
    {
      dataField: "phone",
      text: "Phone",
      formatter: (_, row) => row.phone || "-",
    },
    {
      dataField: "subscriber_status",
      text: "Status",
      formatter: (_, row) => getStatusBadge(row.subscriber_status),
    },
    {
      dataField: "user_type",
      text: "Type",
      formatter: (_, row) => (
        <Badge bg="info">{row.user_type || "manual"}</Badge>
      ),
    },
    {
      dataField: "gym_name",
      text: "Gym",
      formatter: (_, row) => row.gym_name || "-",
    },
    {
      dataField: "open_count",
      text: "Opens",
      formatter: (_, row) => row.open_count || 0,
    },
    {
      dataField: "click_count",
      text: "Clicks",
      formatter: (_, row) => row.click_count || 0,
    },
    {
      dataField: "subscribed_at",
      text: "Subscribed",
      formatter: (_, row) => (
        <small>{formatDateTimezon(row.subscribed_at || row.date_added) || "N/A"}</small>
      ),
    },
  ];

  return (
    <HelmetWrapper title="WTF: Email Subscribers">
      <Container className="bg-cloud-k px-50px gobal-container" fluid>
        <Modal
          isOpen={modValues.isModOpen}
          onRequestClose={() => setModValues(iniModValues)}
          contentLabel="Subscriber Modal"
          ariaHideApp={false}
        >
          <div className="createNewCTOModalContainer mod-width-50p">
            <ModalHeadAndClose
              title={modValues.modName === "import" ? "Import Subscribers" : "Add Subscriber"}
              onClose={() => setModValues(iniModValues)}
            />
            {modValues.modName === "import" ? (
              <BulkImportForm
                listId={listId}
                closeModal={() => setModValues(iniModValues)}
                userInfoData={userInfoData}
                callFunction={() => fetchSubscribers(filters)}
              />
            ) : (
              <SubscriberForm
                listId={listId}
                closeModal={() => setModValues(iniModValues)}
                userInfoData={userInfoData}
                callFunction={() => fetchSubscribers(filters)}
              />
            )}
          </div>
        </Modal>

        <CsvImportModal
          isOpen={showCsvImport}
          onClose={() => setShowCsvImport(false)}
          listId={listId}
          userInfoData={userInfoData}
          onSuccess={() => fetchSubscribers(filters)}
        />

        {/* Header with list info */}
        <Row className="mb-3">
          <Col>
            <Card>
              <Card.Body className="d-flex justify-content-between align-items-center">
                <div>
                  <h5 className="mb-1">{currentList?.name || "Subscribers"}</h5>
                  <small className="text-muted">
                    {currentList?.subscriber_count?.toLocaleString() || 0} total subscribers |
                    {currentList?.active_count?.toLocaleString() || 0} active
                  </small>
                </div>
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handleExport}
                  >
                    Export CSV
                  </button>
                  <button
                    className="btn btn-outline-success"
                    onClick={() => setShowCsvImport(true)}
                  >
                    Import CSV
                  </button>
                  <button
                    className="btn btn-outline-primary"
                    onClick={() => setModValues({ isModOpen: true, modName: "import" })}
                  >
                    Import from Users
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={() => setModValues({ isModOpen: true, modName: "add" })}
                  >
                    + Add Subscriber
                  </button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col>
            <CmnFilters
              from="email-subscribers"
              userInfoData={userInfoData}
              searchFilterApplied={searchFilterApplied}
              filters={allFilters}
              setFilters={setAllFilters}
              onClickSearch={onClickSearch}
              onClickReset={onResetFilters}
              isLoading={emailSubscribersLoading}
              searchPlaceHolder="Search by email or name..."
            />
          </Col>
        </Row>

        <Row>
          <Col>
            <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mt15">
              {emailSubscribersLoading ? (
                <ReactVortoxSpinner />
              ) : (
                <PaymentLinkTable
                  tableData={emailSubscribers?.data}
                  tableColumns={tbColumns}
                  tablePagination={emailSubscribers?.pagination}
                  isPage={isPage}
                  setIsPage={setIsPage}
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                />
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default EmailSubscribers;
